/**
 * src/services/telemetry/MavlinkTelemetryAdapter.ts
 *
 * Consumes MAVLink frames via the backend `/ws/mavlink` relay (preferred)
 * or, when available, a browser Web Serial stream (for directly-wired
 * telemetry radios attached to the user's machine).  Both transports emit
 * the same NormalizedTelemetry shape; the server-side `mavlinkGateway.js`
 * performs the authoritative parse.  Browser-side Web Serial is a
 * pass-through transport that forwards raw bytes to `/ws/mavlink` for
 * parsing rather than re-implementing the MAVLink parser in-browser.
 */

import {
  NormalizedTelemetry, TelemetryProvider, TelemetrySignal,
} from './TelemetryProvider';
import { resolveWsUrl, safeCloseWs } from '../wsBase';

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

function wsUrl(backendUrl: string): string {
  // Explicit backend override (env/opts) wins; otherwise resolve dev-aware
  // (direct to the Node backend in local dev, same-origin in production).
  if (backendUrl) return backendUrl.replace(/\/+$/, '').replace(/^http/, 'ws') + '/ws/mavlink';
  if (typeof window === 'undefined') return '';
  return resolveWsUrl('/ws/mavlink');
}

export interface MavlinkAdapterOptions {
  backendUrl?: string;
  /** If true, request a Web Serial port when start() is called. */
  enableWebSerial?: boolean;
  /** Baud rate for Web Serial (default 57_600). */
  serialBaud?: number;
  /** Bind this MAVLink sysid to this entity once WS connects. */
  bindSysid?: number;
  bindCompid?: number;
  bindEntityId?: string;
}

export class MavlinkTelemetryAdapter implements TelemetryProvider {
  public readonly sourceType = 'mavlink' as const;
  private ws: WebSocket | null = null;
  private serialPort: any | null = null;
  private serialReader: any | null = null;
  private readonly update = new TelemetrySignal<NormalizedTelemetry>();
  private readonly errors = new TelemetrySignal<Error>();
  private readonly opts: MavlinkAdapterOptions;

  constructor(opts: MavlinkAdapterOptions = {}) { this.opts = opts; }

  get isActive(): boolean { return !!this.ws; }

  async start(): Promise<void> {
    await this.connectWs();
    if (this.opts.enableWebSerial) await this.connectWebSerial().catch((err) => this.errors.emit(err));
  }

  private async connectWs(): Promise<void> {
    const url = wsUrl(this.opts.backendUrl || DEFAULT_BACKEND);
    if (!url) { this.errors.emit(new Error('no_ws_url')); return; }
    try {
      const ws = new WebSocket(url);
      ws.addEventListener('open', () => {
        if (this.opts.bindSysid && this.opts.bindEntityId) {
          ws.send(JSON.stringify({
            type: 'bind',
            sysid: this.opts.bindSysid,
            compid: this.opts.bindCompid ?? null,
            entityId: this.opts.bindEntityId,
          }));
        }
      });
      ws.addEventListener('message', (ev) => {
        try {
          const msg = JSON.parse(String(ev.data));
          if (msg.type === 'telemetry' && msg.normalized) {
            this.update.emit(msg.normalized as NormalizedTelemetry);
          }
        } catch { /* ignore */ }
      });
      ws.addEventListener('error', () => this.errors.emit(new Error('mavlink_ws_error')));
      ws.addEventListener('close', () => { this.ws = null; });
      this.ws = ws;
    } catch (err: any) {
      this.errors.emit(new Error(err?.message || 'ws_connect_failed'));
    }
  }

  /**
   * Web Serial ingestion.  The browser forwards raw bytes to the backend
   * over the same /ws/mavlink connection (via a TELEMETRY_FRAME envelope —
   * future extension) OR to `/api/auth/device-link/session/:id/telemetry/mavlink`
   * for parsed frames.  We stub the minimal wiring here: a forthcoming
   * iteration can drop a parsed-frame fast-path when the user's device has
   * node-mavlink-equivalent WebAssembly.
   */
  private async connectWebSerial(): Promise<void> {
    const nav: any = navigator;
    if (!nav?.serial || typeof nav.serial.requestPort !== 'function') {
      throw new Error('web_serial_not_supported');
    }
    try {
      const port = await nav.serial.requestPort();
      await port.open({ baudRate: this.opts.serialBaud || 57_600 });
      this.serialPort = port;
      const reader = port.readable.getReader();
      this.serialReader = reader;
      (async () => {
        while (this.serialReader) {
          try {
            const { value, done } = await reader.read();
            if (done) break;
            if (!value) continue;
            // For now we only track that bytes are flowing; a future
            // iteration can forward them to the backend for parsing.
            this.update.emit({
              entityId: this.opts.bindEntityId ?? null,
              timestamp: Date.now(),
              clientTimestamp: null,
              sourceType: 'mavlink',
              sourceFieldMap: {},
              trustLevel: 'unverified',
              signerPubKeyHash: null, signature: null,
              position: null, velocity: null, heading: null, attitude: null,
              battery: null, health: { linkQuality: null, rssi: null, satellites: null, fixType: null },
              flightMode: null, armed: null, home: null,
              rawSourceSnapshot: { transport: 'web-serial', bytes: value.length },
            });
          } catch (err: any) {
            this.errors.emit(new Error(err?.message || 'web_serial_read_error'));
            break;
          }
        }
      })();
    } catch (err: any) {
      throw new Error(err?.message || 'web_serial_open_failed');
    }
  }

  stop(): void {
    safeCloseWs(this.ws);
    this.ws = null;
    if (this.serialReader) { try { this.serialReader.cancel(); } catch { /* ignore */ } this.serialReader = null; }
    if (this.serialPort) { try { this.serialPort.close(); } catch { /* ignore */ } this.serialPort = null; }
  }

  onUpdate(cb: (rec: NormalizedTelemetry) => void): () => void { return this.update.on(cb); }
  onError(cb: (err: Error) => void): () => void { return this.errors.on(cb); }
}
